import React from "react";
import Image from "next/image";
import { Cormorant_Garamond } from "next/font/google";
import { cn } from "../../lib/utils";

const display = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["400", "500", "600"],
  style: ["italic", "normal"],
});

interface LoadingScreenProps {
  isVisible: boolean;
  progress: number;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  isVisible,
  progress,
}) => {
  const pct = Math.min(100, Math.max(0, Math.round(progress)));

  return (
    <div
      role="status"
      aria-busy={isVisible}
      aria-live="polite"
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-700 ease-out",
        isVisible
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      )}
    >
      <div className="absolute inset-0 bg-[#f3efe6]/97 backdrop-blur-md" />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#faf7f1] via-[#eef2f6]/55 to-[#1c4a6e]/[0.14]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-[#0f2840]/18 to-transparent"
        aria-hidden
      />

      {/* Quiet nautical silhouette — Mediterranean waterline */}
      <svg
        className="pointer-events-none absolute bottom-0 left-0 h-[min(28vh,200px)] w-full text-[#0f2840]/[0.07]"
        viewBox="0 0 1200 80"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          fill="currentColor"
          d="M0,48 C200,32 400,56 600,44 C800,32 1000,52 1200,40 L1200,80 L0,80 Z"
        />
        <path
          fill="currentColor"
          opacity={0.65}
          d="M0,56 C180,68 360,40 540,52 C720,64 900,44 1200,56 L1200,80 L0,80 Z"
        />
      </svg>

      <div
        className={cn(
          "relative z-10 flex max-w-md flex-col items-center gap-8 px-8 text-center sm:gap-10",
          display.className
        )}
      >
        <p className="text-[0.65rem] font-medium uppercase tracking-[0.38em] text-[#0f2840]/65">
          Fiat Licensed Collection
        </p>

        <div className="relative w-40 h-40 sm:w-48 sm:h-48">
          <div
            className="absolute inset-[-18%] rounded-full bg-[#1c4a6e]/[0.06] blur-2xl"
            aria-hidden
          />
          <Image
            src="/caroffshore-logo.png"
            width={192}
            height={192}
            alt="Car Off Shore"
            className="relative h-full w-full object-contain drop-shadow-[0_2px_24px_rgba(15,40,64,0.08)]"
            priority
          />
        </div>

        <div className="w-full max-w-[260px] space-y-4">
          <p className="text-lg leading-snug text-[#0f2840]/88 italic sm:text-xl">
            Dove l&apos;arte incontra il mare
          </p>

          <div className="space-y-2">
            <div
              className="h-[3px] w-full overflow-hidden rounded-full bg-[#0f2840]/10"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#0f2840] via-[#2a5f7a] to-[#0f2840] transition-[width] duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-[0.6rem] font-medium uppercase tracking-[0.28em] text-[#0f2840]/40">
              {pct}%
            </p>
          </div>
        </div>

        <p className="text-[0.65rem] uppercase tracking-[0.32em] text-[#0f2840]/50">
          Fatto in Italia
        </p>

        <span className="sr-only">
          Loading configurator, {pct} percent complete
        </span>
      </div>
    </div>
  );
};
